import { Injectable } from '@angular/core';
import { BaseDataResponse, BaseEntityResponse, LoginParams, WebAPILoginResponse } from '@base/entities/BaseEntities';
import { EnumNotificationDeliveryOptions } from '@core/framework/sysenums.enum';
import { UiService } from '@core/framework/ui.service';
import { AppConfigService } from '@m4u-angular/configuration/app.config.service';
import { firstValueFrom, Observable, of } from 'rxjs';
import { map, mergeMap, tap } from 'rxjs/operators';
import { LoginEndpoint } from './../endpoints/login.endpoint';
import { FrameworkService } from './framework.service';
import { NavigatorService } from './navigator.service';
import { RC } from './resources.service';
import { RouteHistoryService } from './route-history.service';
import { SessionService } from './session.service';
import { EnumCheckCredentialsResult } from './sysenums.enum';
import { ClientFingerprintService } from './client-fingerprint.service';
import { SysGlobals } from './sysglobals';

@Injectable()
export class LoginService {

    constructor(private Navigator: NavigatorService,
        private LoginEndpoint: LoginEndpoint,
        private Framework: FrameworkService,
        private SessionService: SessionService,
        private UI: UiService,
        private routeHistoryService: RouteHistoryService,
        private appConfigService: AppConfigService,
        private clientFingerprintService: ClientFingerprintService
    ) { }

    DoLogin(Params: LoginParams, bExistingSession: boolean, bIsSaveCredentials: boolean, fAfterInit: () => void, fLoginFailed: (responseSession: BaseEntityResponse<WebAPILoginResponse>) => void = null, mfaCodeSent: (deliveryOption: EnumNotificationDeliveryOptions) => void = null) {
        Params.ClientFingerprint = this.clientFingerprintService.GetClientFingerprint();
        return this.LoginEndpoint.Login(Params).pipe(
            mergeMap((response: BaseEntityResponse<WebAPILoginResponse>) => {
                if (!response.IsSuccess) {
                    this.UI.StopMessage(response.ResponseMessage);
                    if (fLoginFailed) fLoginFailed(response);
                    return of(false);
                }
                switch (response.Entity.CheckResult) {
                    case EnumCheckCredentialsResult.Success:
                        return this.OnLoginSuccess(response, bExistingSession, bIsSaveCredentials, Params.Language, fAfterInit);
                    case EnumCheckCredentialsResult.PasswordExpired:
                        this.Navigator.Navigate(['/system/passwordchangeexpired'], { UserName: Params.UserName });
                        return of(false);
                    case EnumCheckCredentialsResult.Failure:
                        this.UI.StopMessage(response.Entity.CheckResultMessage);
                        if (fLoginFailed) fLoginFailed(response);
                        return of(false);
                    case EnumCheckCredentialsResult.MFAEmailSent:
                        mfaCodeSent && mfaCodeSent(EnumNotificationDeliveryOptions.Email);
                        return of(false);
                    case EnumCheckCredentialsResult.MFASMSSent:
                        mfaCodeSent && mfaCodeSent(EnumNotificationDeliveryOptions.SMS);
                        return of(false);
                    case EnumCheckCredentialsResult.NoMID:
                        if (bExistingSession) this.OnLoginBySessionFailed();
                        this.Navigator.Navigate(['/system/midstore']);
                        return of(false);
                    case EnumCheckCredentialsResult.SessionFrozen:
                        this.UI.StopMessage(RC('errorSessionIsFrozen'));
                        if (fLoginFailed) fLoginFailed(response);
                        //this.Navigator.Navigate(['/system/login'], { Language: SysGlobals.Language });
                        return of(false);
                    default:
                        if (bExistingSession) this.OnLoginBySessionFailed();
                        this.UI.StopMessage(response.Entity.CheckResultMessage);
                        if (fLoginFailed) fLoginFailed(response);
                        return of(false);
                }
            })
        );
    }

    //Handle succesfull login
    private OnLoginSuccess(response: BaseEntityResponse<WebAPILoginResponse>, bExistingSession: boolean, bIsSaveCredentials: boolean, sLanguage: string, fAfterInit: () => void) {
        //If system options were not loaded, prevent logging in
        //Do this check only if login was successfull, so that if not the regular message is displayed
        if (!this.Framework.IsFrameworkInitialized()) {
            this.UI.UnexpectedMessage(RC('errorNotInitialized'));
            return of(false);
        }

        //Save session id and language in local storage, or clear if saving was not specified
        if (bIsSaveCredentials && !bExistingSession) {
            this.SessionService.SaveSessionCredentials(response.Entity.SessionID, response.Entity.DisplayUserName);
        }

        if (!bIsSaveCredentials && !bExistingSession) {
            this.SessionService.ClearSessionCredentials();
        }

        //Set session
        return this.SessionService.SetSession(response.Entity, () => {
            this.routeHistoryService.ClearHistory();
            this.routeHistoryService.StartSaveHistory();
            fAfterInit && fAfterInit();
        }, () => {
            this.Navigator.Navigate(['/system/login']);
        });
    }

    //Clear invalid sessionid, and let the user login on his own
    private OnLoginBySessionFailed() {
        this.SessionService.ClearSessionCredentials();
    }

    DoLogout(fAfterLogout: () => void) {
        this.LoginEndpoint.Logout()
            .pipe(
                tap(response => {
                    //Even if there is an error, go to login screen anyway, so the user won't be stuck
                    if (!response.IsSuccess) this.UI.UnexpectedMessage(response.ResponseMessage);
                }))
            .subscribe(() => {
                this.SessionService.SetSession(undefined, null, () => {
                    this.routeHistoryService.ClearHistory();
                    fAfterLogout && fAfterLogout();
                });
            });
    }

    ImpersonateLogin(credentialID: number): Observable<boolean> {
        return this.LoginEndpoint.ImpersonateLogin(credentialID).pipe(mergeMap(async (response: BaseEntityResponse<WebAPILoginResponse>) => {
            if (!response.IsSuccess) {
                response.ResponseMessage && this.UI.StopMessage(response.ResponseMessage);
                return false;
            }

            const defaultRouteParams = await firstValueFrom(
                this.appConfigService.AppConfig.OnGetDefaultRouteParams(response.Entity.UserInfo[this.ModuleName]));
            if (!defaultRouteParams || !defaultRouteParams.Route) return false;

            const params = { ...defaultRouteParams.Params || {}, SessionID: response.Entity.SessionID };
            const url = this.Navigator.GetUrl(defaultRouteParams.Route, params);
            this.Navigator.NavigateExternal(url);
            return true;
        }));
    }

    private get ModuleName(): string {
        const moduleNames = this.appConfigService.AppConfig.ModuleName.split(',');
        return moduleNames[0];
    }
}
